"use client";

import {
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Download,
  Filter,
  SlidersHorizontal,
  X
} from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn, getStatusLabel } from "@/lib/utils";

// Import utility functions for computed values
import { getApplicationPhase } from "@/features/applications/utils/phaseUtils";
import { getQualificationType } from "@/features/applications/utils/qualificationUtils";
import { useTranslation } from "react-i18next";

export interface FilterOperator {
  value: string;
  label: string;
  type: "text" | "select" | "multiselect" | "date";
}

export interface FilterableColumn {
  id: string;
  label: string;
  type: "text" | "select" | "multiselect" | "date" | "number";
  operators: FilterOperator[];
  options?: { value: string; label: string }[];
}

export interface ActiveFilter {
  id: string;
  columnId: string;
  columnLabel: string;
  operator: string;
  operatorLabel: string;
  value: string | string[];
  valueLabel: string;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchKey: string;
  exportData?: () => void;
  filterPlaceholder?: string;
  onRowClick?: (row: { original: TData }) => void;
  initialColumnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  filterableColumns?: FilterableColumn[];
  getRowClassName?: (row: Row<TData>) => string;
}

// Default operators for different column types

// Custom filter function that handles our advanced filters

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  exportData,
  filterPlaceholder = "Filter...",
  onRowClick,
  initialColumnVisibility,
  filterableColumns = [],
  getRowClassName
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation(["applications"]);
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(initialColumnVisibility || {});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [, setPageSize] = React.useState(10);
  const [activeFilters, setActiveFilters] = React.useState<ActiveFilter[]>([]);

  // Separate search state
  const [searchValue, setSearchValue] = React.useState("");

  // Filter creation state
  const [showFilterDropdown, setShowFilterDropdown] = React.useState(false);
  const [selectedColumn, setSelectedColumn] =
    React.useState<FilterableColumn | null>(null);
  const [selectedOperator, setSelectedOperator] =
    React.useState<FilterOperator | null>(null);
  const [filterValue, setFilterValue] = React.useState<string | string[]>("");

  // Column search state
  const [columnSearchValue, setColumnSearchValue] = React.useState("");

  // Helper function to get row value based on column configuration
  const getRowValue = React.useCallback(
    (row: any, column: FilterableColumn) => {
      // Handle computed columns like 'name' that combine multiple fields
      if (column.id === "name") {
        return `${row.given_name || ""} ${row.family_name || ""}`.trim();
      }

      // Handle qualification (computed from multiple fields)
      if (column.id === "qualification") {
        return getQualificationType(row);
      }

      // Handle phase (computed)
      if (column.id === "phase") {
        return getApplicationPhase(row);
      }

      // Handle status (computed)
      if (column.id === "status") {
        return getStatusLabel(row.status, t);
      }

      // Handle expected_start (computed from created_at + 5 months)
      if (column.id === "expected_start") {
        if (row.created_at) {
          const startDate = new Date(row.created_at);
          startDate.setMonth(startDate.getMonth() + 5);
          return startDate.toLocaleDateString("de-DE");
        }
        return null;
      }

      // Handle other columns by their ID or accessor
      return row[column.id] || null;
    },
    [t]
  );

  // Define filter function
  const customFilterFn = React.useMemo(() => {
    return (
      rows: Row<TData>[],
      activeFilters: ActiveFilter[],
      searchValue: string
    ) => {
      console.log("🔍 Custom filter function called with:", {
        rowCount: rows.length,
        activeFilters,
        searchValue
      });

      return rows.filter((row) => {
        const original = row.original as any;

        // Apply search filter first
        if (searchValue) {
          // Get the column definition for search
          const searchColumn = filterableColumns.find(
            (col) => col.id === searchKey
          );

          if (searchColumn) {
            const searchRowValue = getRowValue(original, searchColumn);
            const searchStringValue = String(
              searchRowValue || ""
            ).toLowerCase();

            if (!searchStringValue.includes(searchValue.toLowerCase())) {
              console.log(
                "❌ Search filter failed for row:",
                original,
                "searchValue:",
                searchValue,
                "rowValue:",
                searchStringValue
              );
              return false;
            }
          }
        }

        // Apply all active filters
        for (const filter of activeFilters) {
          const column = filterableColumns.find(
            (col) => col.id === filter.columnId
          );
          if (!column) {
            console.log("❌ Column not found for filter:", filter);
            continue;
          }

          const rowValue = getRowValue(original, column);
          const filterValue = filter.value;

          console.log("🔍 Checking filter:", {
            column: filter.columnId,
            operator: filter.operator,
            filterValue,
            rowValue,
            originalRow: original
          });

          let matches = false;

          switch (filter.operator) {
            case "contains":
              matches = String(rowValue || "")
                .toLowerCase()
                .includes(String(filterValue).toLowerCase());
              break;
            case "equals":
              matches =
                String(rowValue || "").toLowerCase() ===
                String(filterValue).toLowerCase();
              break;
            case "is_empty":
              matches =
                !rowValue ||
                rowValue === "" ||
                (Array.isArray(rowValue) && rowValue.length === 0);
              break;
            case "is_not_empty":
              matches =
                !!rowValue &&
                rowValue !== "" &&
                (!Array.isArray(rowValue) || rowValue.length > 0);
              break;
            default:
              matches = true;
          }

          console.log(`${matches ? "✅" : "❌"} Filter result:`, matches);

          if (!matches) {
            return false;
          }
        }

        return true;
      });
    };
  }, [filterableColumns, searchKey, getRowValue]);

  // Filter the data manually
  const filteredData = React.useMemo(() => {
    if (!activeFilters.length && !searchValue) {
      console.log("📄 No filters applied, returning all data:", data.length);
      return data;
    }

    const rows = data.map((item, index) => ({
      original: item,
      index
    })) as Row<TData>[];

    const filtered = customFilterFn(rows, activeFilters, searchValue);
    console.log("🎯 Filtered results:", filtered.length, "from", data.length);

    return filtered.map((row) => row.original);
  }, [data, activeFilters, searchValue, customFilterFn]);

  const table = useReactTable({
    data: filteredData, // Use pre-filtered data
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility: columnVisibility || {},
      rowSelection
    },
    defaultColumn: {
      cell: ({ getValue }) => getValue()
    }
  });

  // Apply active filters to react-table
  React.useEffect(() => {
    console.log("🎮 Filter change detected:", { searchValue, activeFilters });
    // We don't need to set column filters anymore since we're filtering manually
  }, [activeFilters, searchValue]);

  const addFilter = () => {
    if (!selectedColumn || !selectedOperator) return;

    const needsValue = !["is_empty", "is_not_empty"].includes(
      selectedOperator.value
    );
    if (
      needsValue &&
      (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0))
    ) {
      return;
    }

    let valueLabel = "";
    if (!needsValue) {
      valueLabel = "";
    } else if (Array.isArray(filterValue)) {
      const labels = filterValue.map((val) => {
        const option = selectedColumn.options?.find((opt) => opt.value === val);
        return option?.label || val;
      });
      valueLabel = labels.join(", ");
    } else {
      const option = selectedColumn.options?.find(
        (opt) => opt.value === filterValue
      );
      valueLabel = option?.label || String(filterValue);
    }

    const newFilter: ActiveFilter = {
      id: `${selectedColumn.id}-${selectedOperator.value}-${Date.now()}`,
      columnId: selectedColumn.id,
      columnLabel: selectedColumn.label,
      operator: selectedOperator.value,
      operatorLabel: selectedOperator.label,
      value: filterValue,
      valueLabel
    };

    setActiveFilters((prev) => [...prev, newFilter]);

    // Reset form
    setSelectedColumn(null);
    setSelectedOperator(null);
    setFilterValue("");
    setShowFilterDropdown(false);
  };

  const removeFilter = (filterId: string) => {
    setActiveFilters((prev) => prev.filter((f) => f.id !== filterId));
  };

  const renderFilterValue = () => {
    if (!selectedColumn || !selectedOperator) return null;

    const needsValue = !["is_empty", "is_not_empty"].includes(
      selectedOperator.value
    );
    if (!needsValue) return null;

    if (
      (selectedOperator.type === "select" ||
        selectedOperator.type === "multiselect") &&
      selectedColumn.options
    ) {
      if (selectedOperator.type === "multiselect") {
        const selectedValues = Array.isArray(filterValue) ? filterValue : [];
        const selectedLabels = selectedValues
          .map((val) => {
            const option = selectedColumn.options?.find(
              (opt) => opt.value === val
            );
            return option?.label || val;
          })
          .join(", ");

        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-[200px] justify-between"
              >
                {selectedValues.length > 0
                  ? selectedValues.length === 1
                    ? selectedLabels
                    : `${selectedValues.length} ausgewählt`
                  : "Werte auswählen..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0">
              <Command>
                <CommandInput placeholder="Suchen..." />
                <CommandEmpty>Keine Optionen gefunden.</CommandEmpty>
                <CommandGroup>
                  {selectedColumn.options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => {
                          if (isSelected) {
                            setFilterValue(
                              selectedValues.filter((v) => v !== option.value)
                            );
                          } else {
                            setFilterValue([...selectedValues, option.value]);
                          }
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        );
      } else {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-[200px] justify-between"
              >
                {filterValue
                  ? selectedColumn.options.find(
                      (opt) => opt.value === filterValue
                    )?.label || filterValue
                  : "Wert auswählen..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0">
              <Command>
                <CommandInput placeholder="Suchen..." />
                <CommandEmpty>Keine Optionen gefunden.</CommandEmpty>
                <CommandGroup>
                  {selectedColumn.options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => setFilterValue(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          filterValue === option.value
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        );
      }
    }

    if (selectedOperator.type === "date") {
      return (
        <Input
          type="date"
          value={filterValue as string}
          onChange={(e) => setFilterValue(e.target.value)}
          className="w-[200px]"
        />
      );
    }

    return (
      <Input
        placeholder="Wert eingeben..."
        value={filterValue as string}
        onChange={(e) => setFilterValue(e.target.value)}
        className="w-[200px]"
      />
    );
  };

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex flex-1 items-center space-x-2">
          <Input
            placeholder={filterPlaceholder}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            className="h-8 w-[150px] lg:w-[250px]"
          />

          {/* Add Filter Button */}
          {filterableColumns.length > 0 && (
            <Popover
              open={showFilterDropdown}
              onOpenChange={setShowFilterDropdown}
            >
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="space-y-4">
                  <div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-[200px] justify-between"
                        >
                          {selectedColumn?.label || "Spalte auswählen..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[200px] p-0">
                        <Command>
                          <CommandInput placeholder="Spalte suchen..." />
                          <CommandEmpty>Keine Spalten gefunden.</CommandEmpty>
                          <CommandGroup>
                            {filterableColumns.map((column) => (
                              <CommandItem
                                key={column.id}
                                value={column.id}
                                onSelect={() => {
                                  setSelectedColumn(column);
                                  // Auto-select first operator
                                  const firstOperator = column.operators[0];
                                  setSelectedOperator(firstOperator);
                                  // Initialize filter value based on operator type
                                  if (firstOperator?.type === "multiselect") {
                                    setFilterValue([]);
                                  } else {
                                    setFilterValue("");
                                  }
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedColumn?.id === column.id
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                                {column.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {selectedColumn && (
                    <div>
                      <Select
                        value={selectedOperator?.value || ""}
                        onValueChange={(value) => {
                          const operator = selectedColumn.operators.find(
                            (op) => op.value === value
                          );
                          setSelectedOperator(operator || null);
                          // Initialize filter value based on operator type
                          if (operator?.type === "multiselect") {
                            setFilterValue([]);
                          } else {
                            setFilterValue("");
                          }
                        }}
                      >
                        <SelectTrigger className="w-[140px] h-8">
                          <SelectValue placeholder="Operator auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedColumn.operators.map((operator) => (
                            <SelectItem
                              key={operator.value}
                              value={operator.value}
                            >
                              {operator.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {selectedColumn && selectedOperator && (
                    <div>{renderFilterValue()}</div>
                  )}

                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedColumn(null);
                        setSelectedOperator(null);
                        setFilterValue("");
                        setShowFilterDropdown(false);
                      }}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      size="sm"
                      onClick={addFilter}
                      disabled={!selectedColumn || !selectedOperator}
                    >
                      Filter hinzufügen
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {exportData && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto h-8"
              onClick={exportData}
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="ml-auto h-8">
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Spalten
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px]">
            <div className="p-2">
              <Input
                placeholder="Spalten suchen..."
                value={columnSearchValue}
                onChange={(e) => setColumnSearchValue(e.target.value)}
                className="h-8 mb-2"
              />
            </div>
            {table
              .getAllColumns()
              .filter((column) => {
                if (!column.getCanHide()) return false;
                if (!columnSearchValue) return true;

                const headerText =
                  typeof column.columnDef.header === "string"
                    ? column.columnDef.header
                    : column.id;

                return headerText
                  .toLowerCase()
                  .includes(columnSearchValue.toLowerCase());
              })
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {typeof column.columnDef.header === "string"
                      ? column.columnDef.header
                      : column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeFilters.map((filter) => (
            <Badge key={filter.id} variant="secondary" className="px-3 py-1">
              <span className="font-medium">{filter.columnLabel}</span>
              <span className="mx-2 text-muted-foreground">
                {filter.operatorLabel}
              </span>
              {filter.valueLabel && <span>{filter.valueLabel}</span>}
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => removeFilter(filter.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            "flex items-center space-x-2",
                            header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : ""
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          {header.column.getCanSort() && (
                            <div className="ml-2">
                              {{
                                asc: <ArrowUp className="h-4 w-4" />,
                                desc: <ArrowDown className="h-4 w-4" />
                              }[header.column.getIsSorted() as string] ?? (
                                <ArrowUpDown className="h-4 w-4 opacity-50" />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn(
                    onRowClick ? "cursor-pointer" : "",
                    getRowClassName && getRowClassName(row)
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Keine Ergebnisse.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} Zeile(n) ausgewählt.
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm">Zeilen pro Seite</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
                setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent side="top">
                {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm">
            Seite {table.getState().pagination.pageIndex + 1} von{" "}
            {table.getPageCount()}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Zur ersten Seite</span>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Zur vorherigen Seite</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Zur nächsten Seite</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Zur letzten Seite</span>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
